import { isString } from 'lodash';

import type { Maybe, YardTaskAttachment } from '@nova/core';

export const ATTACHMENT_PDF_MIME_TYPE = 'application/pdf';

/** What an upload control offers, and what the apps are willing to attach. */
export const ATTACHMENT_ACCEPTED_MIME_TYPES = `image/*,${ATTACHMENT_PDF_MIME_TYPE}`;

export const ATTACHMENT_MAX_SIZE_MB = 20;

const ATTACHMENT_MAX_SIZE_BYTES = ATTACHMENT_MAX_SIZE_MB * 1024 * 1024;

/** A task drawer holds a lot to read past; near-opaque leaves only the attachment. */
export const ATTACHMENT_SCRIM_OPACITY = 0.9;

/** Why a file was refused before any request was made. */
export const AttachmentRejection = {
  InvalidType: 'invalid-type',
  TooLarge: 'too-large'
} as const;

export type AttachmentRejectionReason =
  (typeof AttachmentRejection)[keyof typeof AttachmentRejection];

export function isImageAttachment(attachment: YardTaskAttachment): boolean {
  const { mimetype } = attachment || {};
  return Boolean(isString(mimetype) && mimetype.startsWith('image/'));
}

export function isPdfAttachment(attachment: YardTaskAttachment): boolean {
  return attachment.mimetype === ATTACHMENT_PDF_MIME_TYPE;
}

/** Whether the app has a preview for this; anything else is saved rather than opened. */
export function canPreviewAttachment(attachment: YardTaskAttachment): boolean {
  return isImageAttachment(attachment) || isPdfAttachment(attachment);
}

/**
 * Client-side only: the API accepts any file type. The reason is returned rather than a
 * message because the wording — and its translation — belongs to each app.
 */
export function getAttachmentRejection(file: File): Maybe<AttachmentRejectionReason> {
  if (!file.type.startsWith('image/') && file.type !== ATTACHMENT_PDF_MIME_TYPE) {
    return AttachmentRejection.InvalidType;
  }

  if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
    return AttachmentRejection.TooLarge;
  }

  return null;
}
